import { Component } from "@theme/component";
import { scheduler } from "@theme/utilities";

/**
 * PageCountCalculator manages dynamic page count calculation for slideshow controls
 * based on viewport width and CSS variables (--slide-width, --slideshow-gap).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [slideshowControls]
 *
 * @extends {Component<Refs>}
 */
export class SlideshowControls extends Component {
  /**
   * The items count for page calculation
   * @type {number}
   */
  #itemsCount = 0;

  /**
   * The ResizeObserver instance for monitoring container size changes
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * Cached page count value
   * @type {number}
   */
  #pageCount = 1;

  /**
   * Timeout ID for debouncing resize calculations
   * @type {number | undefined}
   */
  #resizeTimeout = undefined;

  /**
   * Scroll container used for touch/trackpad scroll detection
   * @type {HTMLElement | null}
   */
  #scrollContainer = null;

  /**
   * Currently selected page index.
   * @type {number}
   */
  #selectedPageIndex = 0;

  async connectedCallback() {
    super.connectedCallback();

    const pageMode = this.getAttribute("data-page-mode");
    if (!pageMode) {
      return;
    }

    this.#itemsCount = this.getItemsCount();

    if (this.#itemsCount <= 0) return;

    this.#setupPageCountCalculation();
    this.#disableAnimationTimelinesForPageControls();
    this.#setupScrollListener();
    this.#setupSlideshowListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const pageMode = this.getAttribute("data-page-mode");
    if (!pageMode) {
      return;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    if (this.#resizeTimeout !== undefined) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = undefined;
    }

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener("scrollend", this.#handleScrollEnd);
      this.#scrollContainer = null;
    }
  }

  /**
   * Gets the items count from data attribute
   * @returns {number} The items count
   */
  getItemsCount() {
    const count = this.getAttribute("data-items-count");
    const parsed = parseInt(count || "0", 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Gets the current page count
   * @returns {number} The calculated page count
   */
  get pageCount() {
    return this.#pageCount;
  }

  /**
   * Sets up the page count calculation with ResizeObserver
   */
  #setupPageCountCalculation() {
    this.#calculatePageCount();

    scheduler.schedule(() => {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#debounceCalculation();
      });

      this.#resizeObserver.observe(this);
    });
  }

  /**
   * Debounces the page count calculation to avoid excessive recalculations
   */
  #debounceCalculation() {
    if (this.#resizeTimeout !== undefined) {
      clearTimeout(this.#resizeTimeout);
    }

    this.#resizeTimeout = setTimeout(() => {
      this.#calculatePageCount();
      this.#disableAnimationTimelinesForPageControls();
      this.#updateSelectedPageFromScrollPosition();
      this.#resizeTimeout = undefined;
    }, 150);
  }

  /**
   * Calculates the page count based on fully visible slides.
   */
  #calculatePageCount() {
    const slidesPerPage = this.#getSlidesPerPage();
    const pageCount = Math.ceil(this.#itemsCount / slidesPerPage);

    if (this.#pageCount !== pageCount) {
      this.#pageCount = pageCount;
      this.setAttribute("data-page-count", String(pageCount));

      this.#updateControlsCount(pageCount);

      this.dispatchEvent(
        new CustomEvent("pageCountChanged", {
          detail: { pageCount },
          bubbles: true,
          composed: true,
        })
      );
    }

    this.#selectedPageIndex = Math.min(this.#selectedPageIndex, this.#pageCount - 1);
  }

  /**
   * Calculates how many slides are fully visible in one viewport.
   * @returns {number} The number of fully visible slides per page
   */
  #getSlidesPerPage() {
    const slideshow = this.closest("slideshow-component");
    const scrollContainer = slideshow?.querySelector("slideshow-slides");

    if (scrollContainer instanceof HTMLElement) {
      const slides = Array.from(scrollContainer.children).filter(
        (element) => element instanceof HTMLElement
      );

      const firstSlide = slides[0];

      if (firstSlide instanceof HTMLElement) {
        const firstRect = firstSlide.getBoundingClientRect();
        let itemStep = firstRect.width;

        const secondSlide = slides[1];
        if (secondSlide instanceof HTMLElement) {
          const secondRect = secondSlide.getBoundingClientRect();
          const measuredStep = secondRect.left - firstRect.left;

          if (measuredStep > 0) {
            itemStep = measuredStep;
          }
        }

        if (itemStep > 0 && scrollContainer.clientWidth > 0) {
          const gap = Math.max(0, itemStep - firstRect.width);
          return Math.max(1, Math.floor((scrollContainer.clientWidth + gap) / itemStep));
        }
      }
    }

    const computedStyle = window.getComputedStyle(this);
    const slideWidthStr = computedStyle.getPropertyValue("--slide-width").trim();
    const gapStr = computedStyle.getPropertyValue("--slideshow-gap").trim();

    const slideWidth = this.#parseValue(slideWidthStr);
    const gap = this.#parseValue(gapStr);
    const viewportWidth = this.clientWidth;

    if (slideWidth <= 0 || viewportWidth <= 0 || gap < 0) return 1;

    // Fallback only: N fully visible slides use N slide widths and N - 1 gaps.
    return Math.max(1, Math.floor((viewportWidth + gap) / (slideWidth + gap)));
  }

  /**
   * Updates the controls (lines/dots) count dynamically
   * @param {number} newCount - The new page count
   */
  #updateControlsCount(newCount) {
    const pageMode = this.getAttribute("data-page-mode") === "true";

    if (!pageMode) return;

    const linesList = this.querySelector(".slideshow-controls__lines");
    const dotsList = this.querySelector(".slideshow-controls__dots");
    const counterSpan = this.querySelector(".slideshow-controls__counter span");

    if (linesList instanceof HTMLElement) {
      this.#updateLinesList(linesList, newCount);
    }

    if (dotsList instanceof HTMLElement) {
      this.#updateDotsList(dotsList, newCount);
    }

    if (counterSpan instanceof HTMLElement) {
      counterSpan.textContent = String(newCount);
    }

    this.#disableAnimationTimelinesForPageControls();
    this.#applySelectedPage(this.#selectedPageIndex);
  }

  /**
   * Removes scroll-driven animation timelines from page indicators.
   * In page mode, aria-selected should be the only selected-state source.
   */
  #disableAnimationTimelinesForPageControls() {
    const buttons = this.querySelectorAll(
      ".slideshow-controls__lines button, .slideshow-controls__dots button"
    );

    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        button.style.setProperty("animation-timeline", "none");
      }
    });
  }

  /**
   * Updates the lines list with new count
   * @param {HTMLElement} linesList - The lines list element
   * @param {number} newCount - The new count
   */
  #updateLinesList(linesList, newCount) {
    const currentItems = linesList.querySelectorAll("li").length;

    if (currentItems === newCount) return;

    if (currentItems > newCount) {
      for (let i = currentItems - 1; i >= newCount; i--) {
        const item = linesList.querySelector(`li:nth-child(${i + 1})`);
        if (item instanceof HTMLElement) {
          item.remove();
        }
      }
    } else {
      for (let i = currentItems; i < newCount; i++) {
        const li = document.createElement("li");
        const button = document.createElement("button");
        button.className = "slideshow-control";
        button.style.setProperty("animation-timeline", "none");
        button.setAttribute("aria-label", `Page ${i + 1}`);
        button.setAttribute("aria-selected", String(i === this.#selectedPageIndex));
        button.textContent = String(i + 1);

        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.#handlePageClick(i);
        });

        li.appendChild(button);
        linesList.appendChild(li);
      }
    }
  }

  /**
   * Updates the dots list with new count
   * @param {HTMLElement} dotsList - The dots list element
   * @param {number} newCount - The new count
   */
  #updateDotsList(dotsList, newCount) {
    const currentItems = dotsList.querySelectorAll("li").length;

    if (currentItems === newCount) return;

    if (currentItems > newCount) {
      for (let i = currentItems - 1; i >= newCount; i--) {
        const item = dotsList.querySelector(`li:nth-child(${i + 1})`);
        if (item instanceof HTMLElement) {
          item.remove();
        }
      }
    } else {
      for (let i = currentItems; i < newCount; i++) {
        const li = document.createElement("li");
        const button = document.createElement("button");
        button.className = "slideshow-control button button-unstyled";
        button.style.setProperty("animation-timeline", "none");
        button.setAttribute("aria-label", `Page ${i + 1}`);
        button.setAttribute("aria-selected", String(i === this.#selectedPageIndex));
        button.textContent = String(i + 1);

        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.#handlePageClick(i);
        });

        li.appendChild(button);
        dotsList.appendChild(li);
      }
    }
  }

  /**
   * Parses CSS value (%, vw, px) to pixel value
   * @param {string} value - The CSS value to parse
   * @returns {number} The value in pixels
   */
  #parseValue(value) {
    if (!value) return 0;

    if (value.includes("%")) {
      const percent = parseFloat(value);
      return Number.isNaN(percent) ? 0 : (percent / 100) * this.clientWidth;
    }

    if (value.includes("vw")) {
      const vw = parseFloat(value);
      return Number.isNaN(vw) ? 0 : (vw / 100) * window.innerWidth;
    }

    const pixels = parseFloat(value);
    return Number.isNaN(pixels) ? 0 : pixels;
  }

  /**
   * Handles scroll end event. Updates indicator when scroll has fully settled.
   */
  #handleScrollEnd = () => {
    this.#updateSelectedPageFromScrollPosition();
  };

  /**
   * Sets up listener for touch/trackpad scrolling.
   */
  #setupScrollListener() {
    const slideshow = this.closest("slideshow-component");
    if (!slideshow) return;

    const scrollContainer = slideshow.querySelector("slideshow-slides");
    if (!(scrollContainer instanceof HTMLElement)) return;

    this.#scrollContainer = scrollContainer;
    scrollContainer.addEventListener("scrollend", this.#handleScrollEnd);
  }

  /**
   * Sets up listener for slideshow events to sync selected line/dot
   */
  #setupSlideshowListener() {
    const slideshow = this.closest("slideshow-component");

    if (!slideshow) return;

    const eventNames = ["slideshowSelect", "slideshow:select", "select", "change"];

    eventNames.forEach((eventName) => {
      slideshow.addEventListener(eventName, (event) => {
        if (!("detail" in event)) {
          return;
        }

        const detail = event["detail"];

        if (detail && typeof detail === "object" && "index" in detail) {
          const index = detail["index"];
          if (typeof index === "number") {
            // When a real scroll container exists, do not use slideshow events for indicators.
            // During snap/settle, these events can report a centered/transitional item and
            // briefly override the stable scroll-based selection.
            if (this.#scrollContainer) return;

            this.#updateSelectedControl(index);
          }
        }
      });
    });
  }

  /**
   * Updates the selected line/dot based on slide index
   * @param {number} slideIndex - The slide index
   */
  #updateSelectedControl(slideIndex) {
    const pageMode = this.getAttribute("data-page-mode") === "true";

    if (!pageMode) return;

    const pageIndex = this.#getPageIndexFromSlideIndex(slideIndex);
    this.#updateSelectedPage(pageIndex);
  }

  /**
   * Converts slide index to page index based on visible slides per page
   * @param {number} slideIndex - The slide index
   * @returns {number} The page index
   */
  #getPageIndexFromSlideIndex(slideIndex) {
    if (typeof slideIndex !== "number") return 0;

    const slidesPerPage = this.#getSlidesPerPage();
    const pageIndex = Math.floor(slideIndex / slidesPerPage);

    return Math.min(Math.max(0, pageIndex), this.#pageCount - 1);
  }

  /**
   * Updates selected indicators directly by page index and stores it as confirmed state.
   * @param {number} pageIndex - The page index to select
   */
  #updateSelectedPage(pageIndex) {
    const safeIndex = Math.min(Math.max(0, Math.round(pageIndex)), this.#pageCount - 1);
    this.#selectedPageIndex = safeIndex;
    this.#applySelectedPage(safeIndex);
  }

  /**
   * Applies selected indicator visuals without changing confirmed selected state.
   * @param {number} pageIndex - The page index to visually select
   */
  #applySelectedPage(pageIndex) {
    const safeIndex = Math.min(Math.max(0, Math.round(pageIndex)), this.#pageCount - 1);

    const linesList = this.querySelector(".slideshow-controls__lines");
    const dotsList = this.querySelector(".slideshow-controls__dots");

    if (linesList instanceof HTMLElement) {
      this.#updateSelectedInList(linesList, safeIndex);
    }

    if (dotsList instanceof HTMLElement) {
      this.#updateSelectedInList(dotsList, safeIndex);
    }
  }

  /**
   * Updates data-page-selected attribute in a list
   * This is the source of truth for page indicator visual state.
   * Native slideshow may mutate aria-selected, but we use data-page-selected for styling.
   * @param {HTMLElement} list - The list element (lines or dots)
   * @param {number} index - The index to select
   */
  #updateSelectedInList(list, index) {
    const buttons = list.querySelectorAll("button");

    if (buttons.length === 0) return;

    const safeIndex = Math.min(Math.max(0, Math.round(index)), buttons.length - 1);

    buttons.forEach((button, i) => {
      const shouldBeSelected = i === safeIndex;
      button.setAttribute("data-page-selected", String(shouldBeSelected));
    });
  }

  /**
   * Updates the selected indicator after touch/trackpad scroll settles.
   * Uses the actual scroll position as the source of truth.
   */
  #updateSelectedPageFromScrollPosition() {
    const scrollContainer = this.#scrollContainer;
    if (!scrollContainer) return;

    const maxScrollLeft = Math.max(
      0,
      scrollContainer.scrollWidth - scrollContainer.clientWidth
    );

    if (maxScrollLeft <= 0 || this.#pageCount <= 1) {
      this.#updateSelectedPage(0);
      return;
    }

    const maxPageIndex = this.#pageCount - 1;
    const rawPageIndex = Math.round(
      (scrollContainer.scrollLeft / maxScrollLeft) * maxPageIndex
    );
    const safePageIndex = Math.min(Math.max(0, rawPageIndex), maxPageIndex);

    this.#updateSelectedPage(safePageIndex);
  }

  /**
   * Handles page click and navigates to the corresponding slide
   * @param {number} pageIndex - The page index clicked
   */
  #handlePageClick(pageIndex) {
    const slideshow = this.closest("slideshow-component");
    if (!slideshow) {
      return;
    }

    const slidesPerPage = this.#getSlidesPerPage();
    const slideIndex = pageIndex * slidesPerPage;

    const selectFn = "select" in slideshow ? slideshow.select : null;

    if (typeof selectFn === "function") {
      selectFn.call(slideshow, slideIndex, null, { animate: true });
    } else {
      console.warn("slideshow.select not available");
    }
  }
}

if (!customElements.get("slideshow-controls")) {
  customElements.define("slideshow-controls", SlideshowControls);
}